import {
  BaseProps,
  Body,
  Col,
  CommonStyles,
  Flex,
  Image,
  Shape,
  SubTitle,
  styled,
} from '@teko/ds-desktop';
import { ChevronDownOutlined, ChevronUpOutlined } from '@teko/ds-icon';
import { Policy } from '@teko/ecommerce/mobile';
import DOMPurify from 'dompurify';
import { t } from 'i18n';
import React, { ReactElement, useState } from 'react';

export interface Information extends Omit<Policy, 'icon' | 'id'> {
  icon: ReactElement | string;
}

export interface PolicyAndServiceProps extends BaseProps {
  title: string;
  information: Information[];
  max?: number;
  columns?: number;
  showText?: string;
  hideText?: string;
}

export const PolicyAndService: React.FC<PolicyAndServiceProps> = ({
  title,
  information,
  max = 4,
  columns = 2,
  showText = t('Xem thêm'),
  hideText = t('Rút gọn'),
  ...rest
}: PolicyAndServiceProps) => {
  const [maxItems, setMaxItems] = useState(max);

  // map data based on max items allow to display
  const displayInformation =
    maxItems < information.length
      ? information.slice(0, maxItems)
      : information;
  // calculate number of rows to display
  const numberOfRows = Math.round(displayInformation.length / columns);
  const minRows = Math.round(max / columns);

  // split display data into arrays size of display columns
  const displayItems = [];
  for (let index = 0; index < numberOfRows; index++) {
    displayItems.push(
      displayInformation.slice(index * columns, (index + 1) * columns),
    );
  }

  return (
    <Wrapper {...rest}>
      <SubTitle level={2} weight="medium" marginBottom="s12" color="textTitle">
        {title}
      </SubTitle>
      <Shape paddingBottom="s8">
        {displayItems.map((displayItem, displayItemIndex) => {
          return (
            <Flex
              key={displayItemIndex}
              direction="row"
              marginBottom={
                displayItemIndex < displayItems.length - 1 ? 's8' : undefined
              }
              justify="space-between"
              gap="s20"
              className="test-row"
            >
              {displayItem.map((item, itemIndex) => {
                return (
                  <Col
                    key={itemIndex}
                    width={
                      displayItems.length === 1
                        ? '100%'
                        : `calc(${100 / columns}% - 10px)`
                    }
                  >
                    <PolicyItem>
                      {typeof item.icon === 'string' ? (
                        <StyledIcon width={24} height={24} src={item.icon} />
                      ) : (
                        item.icon
                      )}
                      <Content
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.content || '', {
                            ALLOWED_ATTR: ['target', 'href'],
                          }),
                        }}
                      />
                    </PolicyItem>
                  </Col>
                );
              })}
            </Flex>
          );
        })}
      </Shape>
      {(maxItems < information.length || numberOfRows > minRows) && (
        <Flex
          direction="row"
          gap="s8"
          align="center"
          justify="center"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            setMaxItems(
              maxItems < information.length ? information.length : max,
            );
          }}
        >
          <Body level={1} color="textTitle">
            {maxItems < information.length ? showText : hideText}
          </Body>
          {maxItems < information.length ? (
            <ChevronDownOutlined size={10} color="textTitle" />
          ) : (
            <ChevronUpOutlined size={10} color="textTitle" />
          )}
        </Flex>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${CommonStyles};
  background-color: #fff;
  border-radius: 0.5rem;
`;

const PolicyItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Content = styled.div`
  & > * {
    margin: 0;
  }
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #000000;
`;

const StyledIcon = styled(Image)`
  min-width: 24px;
`;
